import React, { FC } from 'react';
import { useSelector, TypedUseSelectorHook } from 'react-redux';

import {
  CertificationRatingStatus,
  ReviewStatus
} from 'types/graphql-api.generated';

import trans from 'common/tools/translations/trans';

import ReviewCardBam from 'website/components/UserOpinion/ReviewCardBam';
import ReviewLink from 'website/components/opinion/ReviewLink';
import StandaloneRating from 'website/components/push/Ratings/StandaloneRating';
import { State } from 'website/reducers';
import { getOpinionForEntity } from 'website/reducers/data/helper';

export type UserReviewOverviewProps = {
  entityId: string;
  title?: string;
  showTitle?: boolean;
  onHasCompleteReviewChange?: (hasReview: boolean) => void;
};

type Opinion = {
  id: string;
  internalId?: number;
  status: ReviewStatus;
  rating?: number;
  review?: string;
  certificationRating?: CertificationRatingStatus | null;
};

const useTypedSelector: TypedUseSelectorHook<State> = useSelector;

const UserReviewOverview: FC<UserReviewOverviewProps> = ({
  entityId,
  title,
  showTitle = false,
  onHasCompleteReviewChange
}) => {
  const opinion = useTypedSelector(state =>
    getOpinionForEntity(entityId, state.data)
  ) as Opinion | null;

  const rating = opinion?.rating;
  const review = opinion?.review;

  const hasRating = Boolean(rating);
  const hasReview = Boolean(review);
  const hasCompleteReview = hasRating && hasReview;

  if (onHasCompleteReviewChange) {
    onHasCompleteReviewChange(hasCompleteReview);
  }

  return (
    <>
      {/* Render review input if review is incomplete */}
      {!hasCompleteReview && (
        <>
          {!opinion?.rating && (
            <>
              <div className="reviews-add-txt">
                {title
                  ? `${trans('seriespage.add_review', { name: title })} ?`
                  : null}
              </div>
              <ReviewLink
                className="js-user-review-link"
                labelClassName="button-inverse-full button-sm"
                entityId={entityId}
                showLabel
                showIcon={false}
              />
            </>
          )}

          {opinion?.rating && (
            <div className="reviews-overview">
              <StandaloneRating
                showDeleteRating={false}
                entityId={entityId}
                showTitle={showTitle}
                title={title}
                withSuggestions={false}
                className="bam-cell user-rating-overview"
                size="large"
              />
              <ReviewLink
                className="js-user-review-link"
                labelClassName="button-inverse-full button-sm"
                entityId={entityId}
                showLabel
                showIcon={false}
              />
            </div>
          )}
        </>
      )}
      {/* Render review card if review is complete */}
      {hasCompleteReview && opinion && (
        <div className="bam-cell user-with-review">
          <ReviewCardBam
            id={opinion.id}
            internalId={opinion.internalId ?? 0}
            entityId={entityId}
            title={title || ''}
            rating={rating ?? 0}
            review={review ?? ''}
            date={new Date()}
            typename="Movie"
            status={opinion.status}
            entityHref={`/movie/${entityId}`}
            certificationRating={opinion.certificationRating}
          />
        </div>
      )}
    </>
  );
};

export default UserReviewOverview;
